/**
 * TransientTransformController — the live drag-preview transform
 * machinery extracted from Scene (#860, epic #828 slice Scene-S5). Scene
 * OWNS a TransientTransformController instance and DRIVES it by
 * composition (it is NOT a base class), mirroring the
 * ConstructionHoverController / ConstructionOverlayRenderer / PieceRenderer
 * precedents and the ConstructionTool controller pattern.
 *
 * This controller holds the per-frame transient-transform cluster used
 * while a rotation / move / mirror / offset drag is in flight: the
 * transient per-line + per-group rotation overrides that tumble the
 * rendered paper items about a pivot without touching the model, the
 * snap-partner translation bookkeeping that keeps constrained partners
 * following the rotated anchor, the bidirectional mirror overlay
 * rotation, the live mirror / offset re-derive that keeps derived
 * children tracking their source, and the pivot crosshair lifecycle.
 *
 * Scene stays the public API: it keeps thin delegators for every public
 * method (~37 external call sites in `src/tools` / `src/app.ts`) so
 * external callers are untouched, keeps the `render()` orchestration
 * (it asks the controller whether a transient pivot crosshair is live
 * before drawing the persistent one), and injects the shared paper
 * scope + root layer + live project + construction-line item lookup and
 * the handful of shared Scene rendering helpers through the
 * `TransientTransformHost` interface. Behaviour is identical to the
 * in-Scene original — this is a pure move + host rewiring.
 */
import paper from "paper";
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  projectConstructionLines,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import {
  computeOffsetPathData,
  computeSingleSegmentOffsetPath,
} from "../model/constructionOffset";
import {
  normalizeRotation,
  rotateConstructionLine,
  rotationPartnerTranslations,
  translateLineForRotationPartner,
} from "../model/constructionRotation";
import { propagateCoincidentAfterGroupMove } from "../model/constraintEnforcement";
import { resolveAxisSpecOnLive, selfAxisGap } from "../model/mirrorAxis";
import { buildMirrorMatrix } from "../model/constructionMirror";
import { resolveOffsetRoot } from "../model/constructionOffset";

/** The shared Scene state + services the transient-transform machinery
 *  reaches back into. The paper scope + root layer + live project + the
 *  rendered construction-line item lookup stay owned by Scene (shared
 *  with the piece / mat / overlay / hover renderers). The construction
 *  crosshair refresh + the handle / halo / display rebuilders + the
 *  rendered-pathData swap + the pivot-crosshair builder + the
 *  live-rotation notifier all stay single-definition on Scene because
 *  non-transient code drives them too. Implemented by Scene. */
export interface TransientTransformHost {
  getPaperScope(): paper.PaperScope;
  getProject(): Project | null;
  getRootLayer(): paper.Layer;
  getConstructionLineItem(constructionId: string): paper.Item | null;
  refreshConstructionCrosshairsLive(): void;
  refreshConstructionHandles(constructionId: string): void;
  refreshConstructionLineHalo(constructionId: string): void;
  refreshConstructionTransformOverlay(constructionId: string): void;
  replaceConstructionLinePath(constructionId: string, pathData: string): void;
  setConstructionLiveStandIn(constructionId: string, pathData: string): void;
  buildRotationPivotCrosshair(cx: number, cy: number): paper.Item;
  notifyRotationLive(spec: { lineId: string; deg: number } | null): void;
}

export class TransientTransformController {
  constructor(private host: TransientTransformHost) {}

  /** The paper scope is owned by Scene and reached through the host. */
  private get scope(): paper.PaperScope {
    return this.host.getPaperScope();
  }

  /** The live project is owned by Scene and reached through the host. */
  private get project(): Project | null {
    return this.host.getProject();
  }

  /** The root layer is owned by Scene and reached through the host. */
  private get rootLayer(): paper.Layer {
    return this.host.getRootLayer();
  }

  /** Rendered path for a project-level construction line, via the host. */
  private getConstructionLineItem(constructionId: string): paper.Item | null {
    return this.host.getConstructionLineItem(constructionId);
  }

  /** Transient rotation delta (degrees) applied to one construction
   *  line's rendered items (yellow path, halo, hairline, rotation
   *  overlay) so the visual tumbles with the cursor during a
   *  rotation drag without going through the model. Applied as a
   *  delta from the previously-applied value on each `set` call. */
  private transientLineRotation: {
    constructionId: string;
    cx: number;
    cy: number;
    deg: number;
    /** Issue #180 bidirectional editing: when the user rotates a
     *  mirror's knob, `constructionId` is the source root but the
     *  rotation OVERLAY (bbox + handle the user is interacting with)
     *  is on the mirror. We track the mirror's per-frame overlay
     *  rotation here so the bbox tilts with the cursor live, not
     *  only on commit. */
    bidirectionalMirror?: {
      mirrorId: string;
      derivedPivot: { x: number; y: number };
      sign: 1 | -1;
    };
  } | null = null;
  /** Crosshair item drawn at the rotation pivot while a transient
   *  rotation drag is active. Stays put across `setTransientLineRotation`
   *  calls (the pivot doesn't move mid-drag), and is removed when the
   *  drag ends with a `null` spec. */
  private rotationPivotCrosshair: paper.Item | null = null;
  /** Crosshair item that follows the cursor while the user is
   *  dragging the rotation pivot itself (issue #420). Distinct from
   *  `rotationPivotCrosshair` (which marks the pivot during a
   *  rotation drag). Rebuilt each frame as the pivot moves; the
   *  always-on persistent marker is suppressed while it's live. */
  private transientPivotCrosshair: paper.Item | null = null;
  /** Per-partner translation state for the current rotation drag —
   *  records how far each snap-partner's rendered items have been
   *  translated since the drag began so the next frame can apply
   *  just the delta (Paper transforms are cumulative). Cleared on
   *  drag end / cancel. */
  private rotationPartnerApplied: Map<
    string,
    { dx: number; dy: number }
  > | null = null;
  /** Partners shown through a live stand-in because the rotation drag
   *  stretches them (#1166). Kept until the drag ends, so a partner that
   *  stops stretching mid-drag is still drawn where it is, not frozen. The
   *  next full render removes the stand-ins. */
  private rotationStretchIds = new Set<string>();
  /** Group-level transient rotation — same shape / lifecycle as the
   *  per-line counterpart but the delta applies to every member item
   *  of `constructionGroupId` plus its bbox overlay. */
  private transientGroupRotation: {
    constructionGroupId: string;
    cx: number;
    cy: number;
    deg: number;
  } | null = null;

  /** True while either pivot crosshair (the rotation-drag marker or the
   *  pivot-drag marker) is live. Read by Scene's `render()` so it can
   *  suppress the always-on persistent pivot crosshair while a transient
   *  one is in flight (issue #420). */
  hasActivePivotCrosshair(): boolean {
    return (
      this.rotationPivotCrosshair !== null ||
      this.transientPivotCrosshair !== null
    );
  }

  /** Push a transient rotation override for an in-progress drag.
   *  Applies the delta (newDeg - lastAppliedDeg) to every rendered
   *  item that belongs to the dragged construction line — yellow
   *  path, halo, hairline, anchor handles, corner handles, AND the
   *  rotation-overlay (so the bbox tilts with the line). Pass `null`
   *  to clear; the next render picks up the committed state. */
  setTransientLineRotation(
    spec: {
      constructionId: string;
      cx: number;
      cy: number;
      deg: number;
      bidirectionalMirror?: {
        mirrorId: string;
        derivedPivot: { x: number; y: number };
        sign: 1 | -1;
      };
    } | null,
  ): void {
    const prev = this.transientLineRotation;
    if (
      (!prev && !spec) ||
      (prev &&
        spec &&
        prev.constructionId === spec.constructionId &&
        prev.cx === spec.cx &&
        prev.cy === spec.cy &&
        prev.deg === spec.deg &&
        prev.bidirectionalMirror?.mirrorId ===
          spec.bidirectionalMirror?.mirrorId)
    ) {
      return;
    }
    const prevDeg = prev ? prev.deg : 0;
    const nextDeg = spec ? spec.deg : 0;
    const targetId = spec ? spec.constructionId : prev?.constructionId;
    const cx = spec ? spec.cx : (prev?.cx ?? 0);
    const cy = spec ? spec.cy : (prev?.cy ?? 0);
    const delta = nextDeg - prevDeg;
    this.transientLineRotation = spec;
    if (targetId && Math.abs(delta) > 1e-6) {
      this.rotateRenderedLineItems(targetId, delta, cx, cy);
    }
    // Bidirectional mirror overlay: when the user is rotating via a
    // mirror's knob, the source's items rotated above but the
    // overlay items (line-rotation-bbox / stem / handle / overlay
    // group) attached to the MIRROR are not in the source's id set.
    // Apply the equivalent derived-space rotation `sign · deg` about
    // the derived pivot directly to the mirror's overlay items so
    // the bbox tilts in lock-step with the cursor. Revert any
    // previously-applied overlay rotation by negating it first
    // (paper transforms are cumulative).
    this.applyTransientMirrorOverlayRotation(prev, spec);
    // Mirrors are NOT in the rigid-rotation set above (their geometry
    // is a reflection across a fixed axis, not a rigid translation),
    // so without an explicit re-derive they stay frozen during the
    // drag. `rederiveMirrorsAffectedBy` reads the live (post-rotate)
    // source pathData off the rendered item and recomputes every
    // mirror downstream — same path the vertex / line-drag flows use
    // to keep mirrors tracking the cursor.
    if (targetId) {
      this.rederiveMirrorsAffectedBy(targetId);
    }
    // Snap-constrained partners follow the rotated anchor in real
    // time — every frame translates each partner by the delta from
    // its previously-applied translation (paper transforms are
    // cumulative, so we track the running total).
    this.applyTransientRotationPartners(
      spec ? new Set([spec.constructionId]) : null,
      spec ? spec.deg : 0,
      spec ? spec.cx : cx,
      spec ? spec.cy : cy,
    );
    // Snap-pair crosshair markers read live anchor positions off the
    // rendered items, so refreshing them after the rotate / translate
    // pass keeps them glued to the moving anchors instead of frozen
    // at their pre-drag spots.
    this.host.refreshConstructionCrosshairsLive();
    // Pivot crosshair lifecycle: a small + at the pivot while the
    // rotation drag is live. Built on first non-null set, removed
    // when the drag clears. Pivot is fixed across the drag so we
    // don't reposition the crosshair on every transient update.
    if (spec && !this.rotationPivotCrosshair) {
      this.rotationPivotCrosshair = this.host.buildRotationPivotCrosshair(
        spec.cx,
        spec.cy,
      );
    } else if (!spec && this.rotationPivotCrosshair) {
      this.rotationPivotCrosshair.remove();
      this.rotationPivotCrosshair = null;
    }
    // Notify live-rotation subscribers with the effective absolute
    // angle (committed `rotation` + transient delta). The scrubber
    // in the properties panel uses this to track the canvas drag
    // without waiting for the commit on drag end.
    let liveSpec: { lineId: string; deg: number } | null = null;
    if (spec && this.project) {
      const line = projectConstructionLines(this.project).find(
        (l) => l.id === spec.constructionId,
      );
      if (line) {
        liveSpec = {
          lineId: spec.constructionId,
          deg: normalizeRotation((line.rotation ?? 0) + spec.deg),
        };
      }
    }
    this.host.notifyRotationLive(liveSpec);
  }

  /** Group-level mirror of `setTransientLineRotation`. Applies the
   *  delta `(newDeg - lastAppliedDeg)` to every rendered item that
   *  belongs to one construction group (members + bbox overlay)
   *  about `(cx, cy)` so the visual tumbles with the cursor during a
   *  group rotation drag. Pivot crosshair lifecycle mirrors the
   *  per-line setter. */
  setTransientGroupRotation(
    spec: {
      constructionGroupId: string;
      cx: number;
      cy: number;
      deg: number;
    } | null,
  ): void {
    const prev = this.transientGroupRotation;
    if (
      (!prev && !spec) ||
      (prev &&
        spec &&
        prev.constructionGroupId === spec.constructionGroupId &&
        prev.cx === spec.cx &&
        prev.cy === spec.cy &&
        prev.deg === spec.deg)
    ) {
      return;
    }
    const prevDeg = prev ? prev.deg : 0;
    const nextDeg = spec ? spec.deg : 0;
    const targetId = spec
      ? spec.constructionGroupId
      : prev?.constructionGroupId;
    const cx = spec ? spec.cx : (prev?.cx ?? 0);
    const cy = spec ? spec.cy : (prev?.cy ?? 0);
    const delta = nextDeg - prevDeg;
    this.transientGroupRotation = spec;
    if (targetId && Math.abs(delta) > 1e-6) {
      this.rotateRenderedGroupItems(targetId, delta, cx, cy);
    }
    // Snap partners + offset descendants of group members follow
    // the rotation in real time — same flow as the per-line drag.
    let memberIds: Set<string> | null = null;
    if (spec && this.project) {
      memberIds = new Set(
        descendantConstructionLineIds(this.project, spec.constructionGroupId),
      );
    }
    this.applyTransientRotationPartners(
      memberIds,
      spec ? spec.deg : 0,
      spec ? spec.cx : cx,
      spec ? spec.cy : cy,
    );
    // Mirrors aren't in the rigid-rotation set (their geometry is a
    // reflection across a fixed axis, not a translation), so without
    // an explicit re-derive they stay frozen during a group drag the
    // same way they did during a single-line drag pre-#180. Walk the
    // group's members (computed for either `spec` or the previous
    // group on drag-end) and re-derive every mirror whose source OR
    // axis line is a member — same path used by the per-line
    // `setTransientLineRotation`.
    const reriveGroupId = spec
      ? spec.constructionGroupId
      : prev?.constructionGroupId;
    if (reriveGroupId && this.project) {
      const members = descendantConstructionLineIds(
        this.project,
        reriveGroupId,
      );
      for (const id of members) {
        this.rederiveMirrorsAffectedBy(id);
      }
    }
    // Snap-pair crosshair markers (issue #67 follow-up) need to
    // travel with their anchors — same rationale as the per-line
    // path.
    this.host.refreshConstructionCrosshairsLive();
    if (spec && !this.rotationPivotCrosshair) {
      this.rotationPivotCrosshair = this.host.buildRotationPivotCrosshair(
        spec.cx,
        spec.cy,
      );
    } else if (!spec && this.rotationPivotCrosshair) {
      this.rotationPivotCrosshair.remove();
      this.rotationPivotCrosshair = null;
    }
  }

  /** Rotate every Paper.js item in the rendered scene that belongs
   *  to one construction line (yellow path, halo, hairline, anchor
   *  / corner handles, and the rotation overlay) about (cx, cy) by
   *  `deg`. Used by both `setTransientLineRotation` (per-frame drag
   *  preview) and the rotation commit-end cleanup path.
   *
   *  Also rotates every (transitive) offset descendant of the line.
   *  An offset is a parallel curve at a fixed perpendicular distance
   *  from its source — rotating both by the same angle about the
   *  same pivot preserves that relationship, and at commit time the
   *  deriveConstructionOffsets pass re-derives the offset from the
   *  rotated source's pathData so the model stays consistent. */
  private rotateRenderedLineItems(
    constructionId: string,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    const idsToRotate = new Set<string>([constructionId]);
    this.collectOffsetDescendants(constructionId, idsToRotate);
    const center = new this.scope.Point(cx, cy);
    for (const child of conGroup.children ?? []) {
      const d = child.data ?? {};
      if (typeof d.constructionId !== "string") continue;
      if (!idsToRotate.has(d.constructionId)) continue;
      child.rotate(deg, center);
    }
  }

  /** Issue #180: rotate ONLY the mirror's overlay items (line-rotation-bbox
   *  / stem / handle / overlay group — anything whose `data.kind` starts
   *  with `line-rotation-`) about (cx, cy) by `deg`. The mirror's PATH
   *  isn't touched here — that's already correctly re-derived from
   *  the rotated source in `rederiveMirrorsAffectedBy`. */
  private rotateMirrorOverlayItems(
    mirrorId: string,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    if (Math.abs(deg) < 1e-6) return;
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    const center = new this.scope.Point(cx, cy);
    for (const child of conGroup.children ?? []) {
      const d = child.data ?? {};
      if (d.constructionId !== mirrorId) continue;
      const kind = typeof d.kind === "string" ? d.kind : "";
      if (!kind.startsWith("line-rotation-")) continue;
      child.rotate(deg, center);
    }
  }

  /** Apply the per-frame overlay delta for a bidirectional mirror
   *  rotation drag. Reverts any rotation applied for the previous
   *  spec's bidirectional mirror (paper transforms are cumulative)
   *  and applies the new one. Both `prev` and `spec` may be null
   *  (drag start / end) or carry different mirror ids (rare but
   *  defensive — e.g. selection changed mid-drag). */
  private applyTransientMirrorOverlayRotation(
    prev: {
      deg: number;
      bidirectionalMirror?: {
        mirrorId: string;
        derivedPivot: { x: number; y: number };
        sign: 1 | -1;
      };
    } | null,
    spec: {
      deg: number;
      bidirectionalMirror?: {
        mirrorId: string;
        derivedPivot: { x: number; y: number };
        sign: 1 | -1;
      };
    } | null,
  ): void {
    const prevBidir = prev?.bidirectionalMirror;
    const nextBidir = spec?.bidirectionalMirror;
    if (prevBidir) {
      const appliedDeg = prevBidir.sign * (prev?.deg ?? 0);
      this.rotateMirrorOverlayItems(
        prevBidir.mirrorId,
        -appliedDeg,
        prevBidir.derivedPivot.x,
        prevBidir.derivedPivot.y,
      );
    }
    if (nextBidir) {
      const nextAppliedDeg = nextBidir.sign * (spec?.deg ?? 0);
      this.rotateMirrorOverlayItems(
        nextBidir.mirrorId,
        nextAppliedDeg,
        nextBidir.derivedPivot.x,
        nextBidir.derivedPivot.y,
      );
    }
  }

  /** Translate snap-constrained partner lines (and any of their
   *  offset descendants) to follow the rotated anchor in real time
   *  during a rotation drag. `rotatedLineIds`-null means "drag
   *  finished": revert any applied translation back to zero so the
   *  next render reads from the freshly-committed model.
   *
   *  Paper transforms are cumulative, so this method tracks the
   *  per-partner running total in `rotationPartnerApplied` and only
   *  applies the delta from the previous frame on each call. */
  private applyTransientRotationPartners(
    rotatedLineIds: ReadonlySet<string> | null,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    if (!this.project) return;
    if (!rotatedLineIds || rotatedLineIds.size === 0) {
      this.rotationStretchIds.clear();
      const prev = this.rotationPartnerApplied;
      if (prev) {
        for (const [partnerId, t] of prev) {
          this.translatePartnerRenderedItems(partnerId, -t.dx, -t.dy);
        }
        this.rotationPartnerApplied = null;
      }
      return;
    }
    const desired = rotationPartnerTranslations(
      this.project,
      rotatedLineIds,
      deg,
      cx,
      cy,
      this.scope,
    );
    const prev = this.rotationPartnerApplied ?? new Map();
    const next = new Map<string, { dx: number; dy: number }>();
    // Apply deltas for every partner the new map mentions.
    for (const [partnerId, t] of desired) {
      const before = prev.get(partnerId) ?? { dx: 0, dy: 0 };
      const dxDelta = t.dx - before.dx;
      const dyDelta = t.dy - before.dy;
      if (Math.abs(dxDelta) > 1e-6 || Math.abs(dyDelta) > 1e-6) {
        this.translatePartnerRenderedItems(partnerId, dxDelta, dyDelta);
      }
      next.set(partnerId, t);
    }
    // Revert any partner that was translated last frame but isn't in
    // the new map (constraint geometry changed mid-drag; rare but
    // defensive).
    for (const [partnerId, t] of prev) {
      if (next.has(partnerId)) continue;
      this.translatePartnerRenderedItems(partnerId, -t.dx, -t.dy);
    }
    this.rotationPartnerApplied = next.size > 0 ? next : null;
    this.applyTransientRotationStretch(rotatedLineIds, desired, deg, cx, cy);
  }

  /** A partner linked to the rotating line at TWO points can't follow by
   *  translating — the commit stretches it so both links stay attached
   *  (#1166). Mirror that live: run the same enforcement the commit does on
   *  the rotated-and-translated geometry, and show every partner whose shape
   *  changes through a stand-in at its stretched path. */
  private applyTransientRotationStretch(
    rotatedLineIds: ReadonlySet<string>,
    translations: Map<string, { dx: number; dy: number }>,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    if (!this.project || (this.project.constraints ?? []).length === 0) return;
    const base = projectConstructionLines(this.project).map((l) => {
      if (rotatedLineIds.has(l.id)) {
        return rotateConstructionLine(l, deg, cx, cy, this.scope, {
          trackRotation: false,
        });
      }
      const t = translations.get(l.id);
      return t ? translateLineForRotationPartner(l, t.dx, t.dy, this.scope) : l;
    });
    const enforced = propagateCoincidentAfterGroupMove(
      this.project,
      base,
      [...rotatedLineIds],
      this.scope,
    );
    if (enforced === base && this.rotationStretchIds.size === 0) return;
    for (let i = 0; i < enforced.length; i++) {
      const l = enforced[i]!;
      if (rotatedLineIds.has(l.id)) continue;
      if (
        l.pathData === base[i]!.pathData &&
        !this.rotationStretchIds.has(l.id)
      ) {
        continue;
      }
      this.rotationStretchIds.add(l.id);
      this.host.setConstructionLiveStandIn(l.id, l.pathData);
    }
  }

  /** Translate one partner line's rendered items by `(dx, dy)`.
   *  Offset descendants are NOT walked here — `rotationPartnerTranslations`
   *  returns a map keyed by every line in the chain (including
   *  every transitive offset descendant) with the correct per-line
   *  delta, so each id translates exactly once. */
  private translatePartnerRenderedItems(
    partnerId: string,
    dx: number,
    dy: number,
  ): void {
    if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return;
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    const delta = new this.scope.Point(dx, dy);
    for (const child of conGroup.children ?? []) {
      const d = child.data ?? {};
      if (typeof d.constructionId !== "string") continue;
      if (d.constructionId !== partnerId) continue;
      child.translate(delta);
    }
  }

  /** Walk the (transitive) offset graph starting from `sourceId`,
   *  pushing every descendant line id into `out`. Iterative BFS so
   *  loops (shouldn't happen with offset graphs but defensive) don't
   *  blow the stack. */
  private collectOffsetDescendants(sourceId: string, out: Set<string>): void {
    if (!this.project) return;
    const queue: string[] = [sourceId];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const l of projectConstructionLines(this.project)) {
        if (l.offsetSource?.sourceId !== cur) continue;
        if (out.has(l.id)) continue;
        out.add(l.id);
        queue.push(l.id);
      }
    }
  }

  /** Translate every rendered item belonging to one construction
   *  GROUP (its member lines' yellow paths / halos / hairlines /
   *  handles, the group's bbox overlay) by the same delta. Mirrors
   *  the per-line `translateConstructionLineRender` but resolved at
   *  the group level via `descendantConstructionLineIds`. */
  rotateRenderedGroupItems(
    groupId: string,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    if (!this.project || Math.abs(deg) < 1e-6) return;
    const lineIds = new Set(
      descendantConstructionLineIds(this.project, groupId),
    );
    // Offset descendants of any group member rotate together with
    // the member — same rationale as the per-line path.
    for (const memberId of [...lineIds]) {
      this.collectOffsetDescendants(memberId, lineIds);
    }
    // Member tracing images (issue #545) rotate with the group too —
    // they orbit the pivot and spin about their own centre, exactly the
    // rigid turn `rotate(deg, center)` applies. Their rasters live at
    // root-layer level (not inside the construction-group), so they're
    // handled in the same pass below.
    const imageIds = new Set(
      descendantConstructionImageIds(this.project, groupId),
    );
    const center = new this.scope.Point(cx, cy);
    for (const child of this.rootLayer.children ?? []) {
      const cd = child.data ?? {};
      if (
        (cd.kind === "tracing-image" || cd.kind === "tracing-image-overlay") &&
        imageIds.has(cd.tracingImageId as string)
      ) {
        child.rotate(deg, center);
        continue;
      }
      if (cd.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        const d = sub.data ?? {};
        if (
          d.constructionId !== undefined &&
          lineIds.has(d.constructionId as string)
        ) {
          sub.rotate(deg, center);
        }
        if (
          d.kind === "construction-group-overlay" &&
          d.constructionGroupId === groupId
        ) {
          sub.rotate(deg, center);
        }
      }
    }
  }

  translateConstructionLineRender(
    constructionId: string,
    dx: number,
    dy: number,
  ): boolean {
    const delta = new this.scope.Point(dx, dy);
    let moved = false;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        const kind = sub.data?.kind;
        if (
          (kind === "construction" ||
            kind === "construction-halo" ||
            kind === "construction-display-stroke" ||
            kind === "construction-halo-display") &&
          sub.data.constructionId === constructionId
        ) {
          sub.translate(delta);
          if (kind === "construction") moved = true;
        }
        // The rotation overlay (issue #67) sits next to the line in
        // the same con-group — translate it in lock-step so the
        // dashed bbox + top handle follow the line during a move
        // drag instead of staying behind until the commit re-renders.
        if (
          kind === "line-rotation-overlay" &&
          sub.data.constructionId === constructionId
        ) {
          sub.translate(delta);
        }
      }
    }
    return moved;
  }

  /** Issue #180 live-mirror counterpart of `rederiveOffsetsOf`:
   *  recompute every (transitive) mirror-derived line whose
   *  `mirrorSource` references `changedId` AS EITHER its source OR
   *  its axis, swap the result into the rendered items in place.
   *  Called from every drag onMove handler so a source-vertex drag,
   *  source-whole-line drag, axis-vertex drag, or axis-whole-line
   *  drag updates the mirror live without waiting for the commit-
   *  time `deriveConstructionMirrors` pass.
   *
   *  Recurses through mirror-of-mirror chains the same way
   *  `rederiveOffsetsOf` recurses through offset-of-offset chains. */
  rederiveMirrorsAffectedBy(changedId: string): void {
    if (!this.project) return;
    const lines = this.project.constructionLines ?? [];
    for (const line of lines) {
      const link = line.mirrorSource;
      if (!link) continue;
      if (
        link.sourceId !== changedId &&
        link.axis.constructionId !== changedId
      ) {
        continue;
      }
      // Pull both source pathData and axis geometry from the LIVE
      // rendered items so the deriver sees the in-flight cursor
      // state, not the pre-drag model snapshot. The source might
      // also be a mirror of something else, so resolve recursively
      // by reading whatever currently sits in the rendered scene.
      const sourceItem = this.getConstructionLineItem(link.sourceId);
      const axisItem = this.getConstructionLineItem(link.axis.constructionId);
      if (!sourceItem || !axisItem) continue;
      const sourcePathData = (sourceItem as paper.PathItem).pathData;
      const axisPathData = (axisItem as paper.PathItem).pathData;
      if (!sourcePathData || !axisPathData) continue;
      // The axis ref addresses an anchor BY INDEX, and only the model geometry
      // says which anchor that index names — it is what the commit-time
      // deriver resolves against. Resolving it against the live path instead
      // (which this used to do, with the matrix inlined) breaks the moment a
      // re-derive rebuilds that line with a different anchor count: an ellipse
      // primitive's offset commits as a clean 4-anchor ellipse but previews as
      // paperjs-offset's 8-anchor one, so `segmentIndex: 2` slid from the right
      // anchor to the top and the copy previewed square ABOVE its source until
      // release put it back on the right (#343).
      const axisModelPath =
        projectConstructionLines(this.project).find(
          (l) => l.id === link.axis.constructionId,
        )?.pathData ?? axisPathData;
      const axisSpec = resolveAxisSpecOnLive(
        axisModelPath,
        axisPathData,
        link.axis,
        this.scope,
      );
      if (!axisSpec) continue;
      // Shared with the commit-time deriver, so the preview cannot drift from
      // what lands on release.
      let { m11, m12, tx, m21, m22, ty } = buildMirrorMatrix(axisSpec);
      let srcCp: paper.CompoundPath | null = null;
      try {
        srcCp = new this.scope.CompoundPath({
          pathData: sourcePathData,
          insert: false,
        });
      } catch {
        continue;
      }
      // Self-axis gap (#343): a copy mirrored across its source's OWN edge is
      // pushed clear by the stored `gapMm`. The commit-time deriver folds this
      // into the same matrix — without it here the mirror snapped flush to its
      // source for the whole drag and then JUMPED to its real position on
      // release, which is the one thing a live preview must never do.
      //
      // Read off the LIVE source, like everything else in this function: the
      // gap is measured from the axis, and during a drag both have moved.
      if (link.gapMm && link.gapMm > 0) {
        // Measured from the offset ROOT's live geometry, not this line's own
        // source — a source and its offset descendants mirror as ONE thing and
        // must share one push vector, or a non-concentric sibling (a
        // single-EDGE offset is an arc off to one side) drifts away from its
        // partner. Same rule as `gapReferenceCenter` in the commit-time
        // deriver; here it reads the LIVE item so it tracks the drag.
        const rootId = resolveOffsetRoot(this.project, link.sourceId);
        const rootItem =
          rootId === link.sourceId
            ? sourceItem
            : (this.getConstructionLineItem(rootId) ?? sourceItem);
        const rb = (rootItem as paper.PathItem).bounds;
        const centre = { x: rb.center.x, y: rb.center.y };
        const g = selfAxisGap(axisSpec, centre, link.gapMm);
        tx += g.dx;
        ty += g.dy;
      }
      const mat = new this.scope.Matrix(m11, m21, m12, m22, tx, ty);
      srcCp.transform(mat);
      const derivedPath = srcCp.pathData;
      srcCp.remove();
      if (!derivedPath) continue;
      this.host.replaceConstructionLinePath(line.id, derivedPath);
      // Adornment items (bezier handles, anchor dots, halo) are
      // derived from segments / bounds of the rendered path, so
      // they go stale the moment `replaceConstructionLinePath` swaps
      // in new pathData. Without these refreshes a user dragging the
      // source (or rotating it, or routing a bidirectional handle /
      // curve drag through it) would see the mirror's yellow line
      // track the cursor while its handles + halo float at their
      // pre-drag positions until commit. All three helpers are
      // idempotent rebuilders gated on their own visibility flag —
      // cheap to call per frame.
      this.host.refreshConstructionHandles(line.id);
      this.host.refreshConstructionLineHalo(line.id);
      // …and the same goes for the selected line's rotate/resize overlay:
      // grab a mirror and its dashed bbox, rotation knob and resize squares
      // sat at the pre-drag position for the whole drag (#343). Not while
      // that mirror's own knob is rotating it: the rebuild reads the stored
      // `rotation`, which only changes on commit, so it would replace the
      // overlay `applyTransientMirrorOverlayRotation` just tilted with an
      // upright one (#1190).
      if (
        this.transientLineRotation?.bidirectionalMirror?.mirrorId !== line.id
      ) {
        this.host.refreshConstructionTransformOverlay(line.id);
      }
      // Recurse — a mirror of this mirror picks up the live pathData
      // we just swapped in.
      this.rederiveMirrorsAffectedBy(line.id);
      // Also re-derive any offset descendants of this newly-updated
      // mirror so chains like "mirror → its offset" stay in sync.
      this.rederiveOffsetsOf(line.id);
    }
  }

  /** Recompute every (transitive) offset-derived construction line
   *  of `sourceId` from the source's *live* rendered pathData and
   *  swap the result into the derived rendered items in place
   *  (issue #29 follow-up). Used by vertex / curve / handle drags
   *  where the source's geometry has changed shape (not just
   *  translated), so a plain item-level translate won't keep the
   *  offset correct.
   *
   *  The Paper.js item swap mutates the rendered scene without
   *  touching the project model; the commit-time
   *  `deriveConstructionOffsets` pass re-runs the same computation
   *  on the new source pathData so the persisted model stays in
   *  sync once the drag releases. */
  rederiveOffsetsOf(sourceId: string): void {
    if (!this.project) return;
    const sourceItem = this.getConstructionLineItem(sourceId);
    if (!sourceItem) return;
    const sourcePathData = (sourceItem as paper.PathItem).pathData;
    if (!sourcePathData) return;
    const lines = this.project.constructionLines ?? [];
    for (const line of lines) {
      if (line.offsetSource?.sourceId !== sourceId) continue;
      const link = line.offsetSource;
      // Single-segment offset (issue #681): re-derive just the one
      // edge, mirroring the commit-time deriver, so a live source edit
      // doesn't transiently pop the whole-outline offset. The rendered
      // `sourcePathData` is already in screen space (rotation baked in),
      // so pass it as a rotation-free synthetic source to avoid the
      // double rotation that `computeSingleSegmentOffsetPath` would
      // otherwise apply.
      const derivedPath = link.segment
        ? computeSingleSegmentOffsetPath(
            { pathData: sourcePathData } as ConstructionLine,
            link.distance,
            link.segment,
            this.scope,
          )
        : computeOffsetPathData(sourcePathData, link.distance, this.scope);
      if (!derivedPath) continue;
      this.host.replaceConstructionLinePath(line.id, derivedPath);
      // Recurse — offsets of offsets re-derive from the freshly-
      // updated derived item we just swapped.
      this.rederiveOffsetsOf(line.id);
      // Issue #180: an offset's mirror-of-offset auto-sibling
      // depends on this offset's pathData. Without this fan-out the
      // mirror sibling stays stale until the next commit fires
      // `deriveAllConnections`. The mirror walker below recurses
      // into its own offset/mirror dependents.
      this.rederiveMirrorsAffectedBy(line.id);
    }
  }

  /** Show / move / hide the crosshair that tracks the cursor while
   *  the rotation pivot is being dragged (issue #420). Rebuilt each
   *  frame (cheap — two line segments) so it follows the snapped
   *  pivot position. While live, the always-on persistent markers are
   *  removed so only the dragging crosshair shows; the next full
   *  render redraws the persistent marker at the committed location. */
  setTransientPivot(spec: { x: number; y: number } | null): void {
    if (this.transientPivotCrosshair) {
      this.transientPivotCrosshair.remove();
      this.transientPivotCrosshair = null;
    }
    if (spec) {
      this.removePersistentPivotMarkers();
      this.transientPivotCrosshair = this.host.buildRotationPivotCrosshair(
        spec.x,
        spec.y,
      );
    }
  }

  /** Remove any always-on pivot markers currently in the scene so a
   *  transient drag crosshair doesn't double up with them. */
  private removePersistentPivotMarkers(): void {
    const items = this.rootLayer.getItems({
      match: (it: paper.Item) =>
        (it.data as { kind?: string } | undefined)?.kind ===
        "rotation-pivot-marker",
    });
    for (const it of items) it.remove();
  }
}
